
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable, firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { ChatService } from '../../features/chat/services/chat.service';
import { API_URL, OPENAI_API_KEY, ANTHROPIC_API_KEY, emails } from '../../config';
import OpenAI from 'openai';
import { ToastService } from './toast.service';

export interface Message {
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  copied?: boolean;
}
export interface PromptMemory {
  id: string;
  prompt: string;
  name: string;
  consultantName?: string;
  presentRole?: string;
  interviewingRole?: string;
  interviewingClient?: string;
  presentClient?: string;
  previousClient?: string;
  interviewType?: string;
  jobDescription?: string;
}
export interface QuestionDto {
  promptId: string;
  chatThreadId: string;
  question: string;
}

export interface SummaryResponse {
  title: string;
  summary: string;
}
export interface ChatHistory {
  s3Url: any;
  id: string;
  promptId: string;
  chatThreadId: string;
  question: string;
  answer: string;
  timestamp: string;
}

export interface aiKeyStatusDTO {
  id: string;
  error: string;
  aiService?: string;
  status: string;
  createdAT: Number;
}

export interface ApiKeys {
  openAiKey: string;
  claudeAiKey: string;
}

@Injectable({
  providedIn: 'root'
})
export class OpenAiService {
  /**
   * Stream Claude answer using built context from frontend
   */

  serviceStatus: any;

  private openAiKey: string = '';
  private claudeAiKey: string = '';

  
  constructor(private http: HttpClient, private toast: ToastService) { }

  async streamClaudeAnswerWithContext(
    context: Array<{ role: string; content: string }>,
    botMessageIndex: number,
    chatService: any,
    onUpdate?: (partial: string) => void,
    abortSignal?: AbortSignal,
    model: string = 'claude-sonnet-4-5-20250929',
    interviewType?: string,
    skipStatusCheck: boolean = false
  ): Promise<void> {
    const messages = context.map(msg => ({ role: msg.role, content: msg.content }));
    if (!skipStatusCheck) {
      try {
        const statuses = this.serviceStatus;
        const svc = (s: any) => String(s?.aiService || s?.aiservice || '').toLowerCase();
        const claudeInactive = statuses.some((s: any) => svc(s) === 'claude' && String(s.status).toLowerCase() === 'inactive');
        if (claudeInactive) {
          try {
            await this.streamAskQuestionWithContext(context, botMessageIndex, chatService, onUpdate, abortSignal, 'gpt-5.2', interviewType, true);
            return;
          } catch (e) {
            const anyErr = e as any;
            if (anyErr?.name === 'AbortError' || anyErr?.code === 20 || anyErr?.type === 'aborted') {
              chatService.finalizeConversation();
              return;
            }
            console.error('Immediate fallback to OpenAI failed (Claude inactive):', e);
            chatService.updateBotMessage(botMessageIndex, "Sorry — we're unable to reach our AI right now. Please try again in a few minutes.");
            chatService.finalizeConversation();
            return;
          }
        }
      } catch (e) {
        console.warn('Could not check Claude status before request, proceeding with Claude attempt', e);
      }
    }
    let retryCount = 0;
    const maxRetries = 3;

    // const hasInactiveStatus = async (serviceName?: string): Promise<boolean> => {
    //   try {
    //     await this.refreshAiKeyStatusesIfNeeded(480);
    //     const statuses: any = await this.getAiKeyStatus().pipe(take(1)).toPromise();
    //     const safeStatuses = Array.isArray(statuses) ? statuses : [];
    //     return safeStatuses.some((status) => {
    //       const isInactive = String(status?.status).toLowerCase() === 'inactive';
    //       if (!isInactive) return false;
    //       if (!serviceName) return true;
    //       const svc = String(status?.aiService || status?.aiservice || '').toLowerCase();
    //       return svc === serviceName.toLowerCase();
    //     });
    //   } catch (e) {
    //     return false;
    //   }
    // };

    const recordErrorAndNotify = async (errorCode: string, errorMessage: string, statusCode?: string | number) => {
      await this.upsertAiKeyStatus('claude', errorCode, errorMessage, statusCode);
    };

    while (retryCount < maxRetries) {
      try {
        const response = await fetch('https://api.anthropic.com/v1/messages', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': this.claudeAiKey || ANTHROPIC_API_KEY,
            'anthropic-version': '2023-06-01',
            'anthropic-dangerous-direct-browser-access': 'true',
          },
          body: JSON.stringify({
            model: model,
            max_tokens: 1024,
            messages: messages,
            stream: true,
          }),
          signal: abortSignal,
        });

        if (!response.ok) {
          throw { message: `API responded with status ${response.status}`, status: response.status, code: String(response.status), headers: response.headers };
        }

        if (!response.body) {
          throw new Error('No response body returned from Claude API');
        }
        console.log('Claude streaming response started', response);
        const reader = response.body.getReader();
        let fullText = '';
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value);
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed.startsWith('data:')) continue;

            const jsonStr = trimmed.replace('data:', '').trim();
            if (jsonStr === '[DONE]') {
              chatService.finalizeConversation();
              return;
            }

            try {
              const parsed = JSON.parse(jsonStr);
              if (parsed.type === 'content_block_delta') {
                const delta = parsed.delta?.text;
                if (delta) {
                  fullText += delta;
                  chatService.updateBotMessage(botMessageIndex, fullText);
                  if (onUpdate) onUpdate(fullText);
                }
              }
            } catch (e) {
              console.error('Error parsing response data:', e);
            }
          }
        }

        chatService.finalizeConversation();
        break;

      } catch (err) {
        // If the provided abortSignal was triggered by the user (clicking stop),
        // prefer to short-circuit immediately and avoid retry logic.
        if (abortSignal?.aborted) {
          chatService.finalizeConversation();
          return;
        }
        const anyErr = err as any;
        if (anyErr?.name === 'AbortError' || anyErr?.code === 20 || anyErr?.type === 'aborted') {
          chatService.finalizeConversation();
          return;
        }

        console.log('Claude stream error encountered:', err);
        retryCount++;
        console.log(`Claude retry attempt ${retryCount} of ${maxRetries}`);

        // After the first failed attempt, check status: if inactive, immediate fallback
        // if (retryCount === 1) {
        //   try {
        //     const inactiveNow = await hasInactiveStatus('claude');
        //     if (inactiveNow) {
        //       // immediate fallback to OpenAI
        //       try {
        //         await this.streamAskQuestionWithContext(context, botMessageIndex, chatService, onUpdate, abortSignal, 'gpt-5.2', interviewType);
        //       } catch (fallbackErr) {
        //         const anyErr = err as any;
        //         if (anyErr?.name === 'AbortError' || anyErr?.code === 20 || anyErr?.type === 'aborted') {
        //           chatService.finalizeConversation();
        //           return;
        //         }
        //         console.error('Fallback to OpenAI failed after detecting inactive status (Claude):', fallbackErr);
        //         chatService.updateBotMessage(botMessageIndex, 'Error: Could not reach either Claude or OpenAI service');
        //         chatService.finalizeConversation();
        //         return;
        //       }
        //       chatService.finalizeConversation();
        //       return;
        //     }
        //   } catch (e) {
        //     // ignore status-check errors
        //   }
        // }

        // If not last attempt, show retry message and wait then retry
        if (retryCount < maxRetries) {
          chatService.updateBotMessage(botMessageIndex, 'Thinking...');
          await new Promise(res => setTimeout(res, 1000));
          continue;
        }

        // Final failure: record and notify, then fallback
        const extractedError = (() => {
          if (err && typeof err === 'object') {
            const anyErr = err as any;
            const statusFromErr = anyErr?.status ?? anyErr?.statusCode ?? anyErr?.response?.status
              ?? ((anyErr?.headers && typeof anyErr.headers.get === 'function') ? (anyErr.headers.get('status') || anyErr.headers.get('x-status-code')) : anyErr?.headers ?? undefined);
            return {
              code: anyErr?.code ?? String(statusFromErr ?? 'UNKNOWN_ERROR'),
              message: anyErr?.message ?? String(anyErr) ?? 'Unknown error during Claude request',
              status: statusFromErr
            } as any;
          }
          return { code: 'UNKNOWN_ERROR', message: String(err) || 'Unknown error during Claude request' } as any;
        })();

        await recordErrorAndNotify(extractedError.code, extractedError.message, extractedError.status);

        // Fallback to OpenAI if configured
        if (interviewType === 'finalround') {
          try {
            await this.streamAskQuestionWithContext(context, botMessageIndex, chatService, onUpdate, abortSignal, 'gpt-5.2', interviewType, true);
            return;
          } catch (fallbackError) {
            const anyErr = err as any;
            if (anyErr?.name === 'AbortError' || anyErr?.code === 20 || anyErr?.type === 'aborted') {
              chatService.finalizeConversation();
              return;
            }
            console.error('Fallback to OpenAI failed after Claude error and marking inactive:', fallbackError);
            chatService.updateBotMessage(botMessageIndex, 'Our Otter AI assistant is temporarily unavailable. Please refresh the page and try again in a few minutes.');
            chatService.finalizeConversation();
            return;
          }
        } else {
          chatService.updateBotMessage(botMessageIndex, 'Something went wrong while generating a response. Please try sending your message again.');
          chatService.finalizeConversation();
          return;
        }
      }
    }
  }

  /**
   * Upload base64 image/file to backend and get S3 URL
   */
  uploadBase64File(request: { base64Content: string, fileExtension?: string }): Observable<string> {
    // Default fileExtension to .jpg if not provided
    const body = {
      base64Content: request.base64Content,
      fileExtension: request.fileExtension || '.jpg'
    };
    return this.http.post<string>(`${API_URL}/uploadScreenshotBase64`, body);
  }

  /**
   * Upload text file to backend and get S3 URL
   */
  uploadTextFile(request: { textContent: string, fileName: string }): Observable<string> {
    return this.http.post<string>(`${API_URL}/uploadText`, request);
  }
  // Expected backend endpoints:
  // GET /api/prompts/:promptId -> { prompt: string }
  // GET /api/chat-history?promptId=... -> [{ question, answer, timestamp }]
  // POST /api/openai-stream { messages: [...] } -> streams OpenAI response
  // GET /api/ask?promptId=...&question=...&answer=... -> saves answer
  /**
   * Stream OpenAI answer using built context from frontend
   */
  // async streamAskQuestionWithContext(
  //   context: Array<{ role: string; content: string }>,
  //   botMessageIndex: number,
  //   chatService: any,
  //   onUpdate?: (partial: string) => void,
  //   abortSignal?: AbortSignal,
  //   model: string = 'gpt-4o',
  //   interviewType?: string
  // ): Promise<void> {
  //   const openai = new OpenAI({
  //     apiKey: OPENAI_API_KEY,
  //     dangerouslyAllowBrowser: true,
  //     timeout: 15000, // 15 second timeout
  //     maxRetries: 2,
  //   });

  //   let fullText = '';

  //   try {
  //     // Optimize input text construction
  //     const inputText = context.reduce((acc, m) => acc + m.content + '\n', '');

  //     const stream = await openai.responses.create(
  //       {
  //         model,
  //         input: inputText,
  //         stream: true,
  //         temperature: 0.1,
  //       },
  //       { signal: abortSignal }
  //     );

  //     for await (const event of stream) {
  //       if (event.type === 'response.output_text.delta' && typeof event.delta === 'string') {
  //         fullText += event.delta;
  //         chatService.updateBotMessage(botMessageIndex, fullText);
  //         if (onUpdate) onUpdate(fullText);
  //       }
  //     }
  //   } catch (err) {
  //     console.error('OpenAI Stream Error:', err);

  //     // Fallback logic if OpenAI fails
  //     try {
  //       if (interviewType === 'firstround') {
  //         // Try Claude Sonnet 4.5 as a fallback
  //         await this.streamClaudeAnswerWithContext(
  //           context,
  //           botMessageIndex,
  //           chatService,
  //           onUpdate,
  //           abortSignal,
  //           'claude-sonnet-4-5-20250929',
  //           interviewType
  //         );
  //         return;
  //       }

  //       if (interviewType === 'practice' || interviewType === 'screening') {
  //         // Try older Claude 3.7 as a fallback
  //         // Try Claude Sonnet 4.5 as a fallback
  //         await this.streamClaudeAnswerWithContext(
  //           context,
  //           botMessageIndex,
  //           chatService,
  //           onUpdate,
  //           abortSignal,
  //           'claude-sonnet-4-5-20250929',
  //           interviewType
  //         );
  //         return;
  //       }
  //     } catch (fallbackErr) {
  //       console.error('Fallback attempt after OpenAI failure also failed:', fallbackErr);
  //       // If fallback also fails, inform the user
  //       chatService.updateBotMessage(botMessageIndex, 'Error: Could not reach Claude or OpenAI services');
  //     }

  //     chatService.updateBotMessage(botMessageIndex, 'Error: Could not process the response');
  //   } finally {
  //     chatService.finalizeConversation();
  //   }
  // }

  async streamAskQuestionWithContext(
    context: Array<{ role: string; content: string }>,
    botMessageIndex: number,
    chatService: any,
    onUpdate?: (partial: string) => void,
    abortSignal?: AbortSignal,
    model: string = 'gpt-4o',
    interviewType?: string
    , skipStatusCheck: boolean = false
  ): Promise<void> {
    if (!skipStatusCheck) {
      try {
        const statuses = this.serviceStatus;
        const svc = (s: any) => String(s?.aiService || s?.aiservice || '').toLowerCase();
        const openaiInactive = statuses.some((s: any) => svc(s) === 'openai' && String(s.status).toLowerCase() === 'inactive');
        if (openaiInactive) {
          const claudeInactive = statuses.some((s: any) => svc(s) === 'claude' && String(s.status).toLowerCase() === 'inactive');
          if (claudeInactive) {
            chatService.updateBotMessage(botMessageIndex, 'Our AI services are temporarily unavailable. Please try again in a few minutes.');
            chatService.finalizeConversation();
            return;
          }
          // route to Claude fallback and skip its status-check to avoid recursion
          let fallbackModel = '';
          if (interviewType === 'firstround') {
            fallbackModel = 'claude-sonnet-4-5-20250929';
          } else if (interviewType === 'practice' || interviewType === 'screening') {
            fallbackModel = 'claude-sonnet-4-5-20250929';
          } else {
            fallbackModel = 'claude-sonnet-4-5-20250929';
          }
          try {
            await this.streamClaudeAnswerWithContext(context, botMessageIndex, chatService, onUpdate, abortSignal, fallbackModel, interviewType, true);
            return;
          } catch (e) {
            console.error('Immediate fallback to Claude failed (OpenAI inactive):', e);
            chatService.updateBotMessage(botMessageIndex, 'Our AI services are temporarily unavailable. Please try again in a few minutes.');
            chatService.finalizeConversation();
            return;
          }
        }
      } catch (e) {
        console.warn('Could not check OpenAI/Claude status before request, proceeding with OpenAI attempt', e);
      }
    }

    let retryCount = 0;
    const maxRetries = 3;
    let fullText = '';


    // const hasInactiveStatus = async (serviceName?: string): Promise<boolean> => {
    //   try {
    //     // Ensure stale inactive records are refreshed to active if past threshold
    //     await this.refreshAiKeyStatusesIfNeeded(480);
    //     const statuses: any = await this.getAiKeyStatus().pipe(take(1)).toPromise();
    //     const safeStatuses = Array.isArray(statuses) ? statuses : [];
    //     return safeStatuses.some((status) => {
    //       const isInactive = String(status?.status).toLowerCase() === 'inactive';
    //       if (!isInactive) return false;
    //       if (!serviceName) return true;
    //       const svc = String(status?.aiService || status?.aiservice || '').toLowerCase();
    //       return svc === serviceName.toLowerCase();
    //     });
    //   } catch (e) {
    //     return false;
    //   }
    // };

    const recordErrorAndNotify = async (errorCode: string, errorMessage: string, statusCode?: string | number) => {
      await this.upsertAiKeyStatus('openai', errorCode, errorMessage, statusCode);
    };

    while (retryCount < maxRetries) {
      try {
        const inputText = context.reduce((acc, m) => acc + m.content + '\n', '');

        const openai = new OpenAI({
          apiKey: this.openAiKey || OPENAI_API_KEY,
          dangerouslyAllowBrowser: true,
          timeout: 15000,
          maxRetries: 0,
        });

        const stream = await openai.responses.create(
          {
            model,
            input: inputText,
            stream: true,
            temperature: 0.1,
          },
          { signal: abortSignal }
        );

        fullText = '';
        for await (const event of stream) {
          if (event.type === 'response.output_text.delta' && typeof event.delta === 'string') {
            fullText += event.delta;
            chatService.updateBotMessage(botMessageIndex, fullText);
            if (onUpdate) onUpdate(fullText);
          }
        }

        break; // success

      } catch (err) {
        // If the caller's abortSignal has been triggered (user pressed stop),
        // short-circuit and do not run retry/fallback logic.
        if (abortSignal?.aborted) {
          chatService.finalizeConversation();
          return;
        }
        // Check if this was an AbortError from the underlying SDK/DOM - if so, exit gracefully
        const anyErr = err as any;
        if (anyErr?.name === 'AbortError' || anyErr?.code === 20 || anyErr?.type === 'aborted') {
          chatService.finalizeConversation();
          return;
        }

        retryCount++;

        // After the first failed attempt, check current status: if inactive, immediately fallback
        // if (retryCount === 1) {
        //   console.log('Checking OpenAI inactive status after first failure');
        //   try {
        //     const inactiveNow = await hasInactiveStatus('openai');
        //     if (inactiveNow) {
        //       console.log('OpenAI marked inactive, attempting immediate fallback to Claude');
        //       // immediate fallback to Claude variants depending on interviewType
        //       let fallbackModel = '';
        //       if (interviewType === 'firstround') {
        //         fallbackModel = 'claude-sonnet-4-5-20250929';
        //       } else if (interviewType === 'practice' || interviewType === 'screening') {
        //         fallbackModel = 'claude-sonnet-4-5-20250929';
        //       }
        //       if (fallbackModel) {
        //         try {
        //           await this.streamClaudeAnswerWithContext(context, botMessageIndex, chatService, onUpdate, abortSignal, fallbackModel, interviewType);
        //         } catch (fallbackErr) {
        //           const anyErr = err as any;
        //           if (anyErr?.name === 'AbortError' || anyErr?.code === 20 || anyErr?.type === 'aborted') {
        //             chatService.finalizeConversation();
        //             return;
        //           }
        //           console.error('Fallback to Claude failed after detecting inactive status:', fallbackErr);
        //         }
        //         chatService.finalizeConversation();
        //         return;
        //       }
        //     }
        //   } catch (e) {
        //     // ignore status-check errors and proceed with retries
        //   }
        // }

        if (retryCount < maxRetries) {
          chatService.updateBotMessage(botMessageIndex, 'Thinking...');
          await new Promise(res => setTimeout(res, 1000));
          continue;
        }

        const extractedError = (() => {
          if (err && typeof err === 'object') {
            const anyErr = err as any;
            const statusFromErr = anyErr?.status ?? anyErr?.statusCode ?? anyErr?.response?.status
              ?? ((anyErr?.headers && typeof anyErr.headers.get === 'function') ? (anyErr.headers.get('status') || anyErr.headers.get('x-status-code')) : anyErr?.headers ?? undefined);
            return {
              code: anyErr?.code ?? String(statusFromErr ?? 'UNKNOWN_ERROR'),
              message: anyErr?.message ?? String(anyErr) ?? 'Unknown error during OpenAI request',
              status: statusFromErr
            } as any;
          }
          return { code: 'UNKNOWN_ERROR', message: String(err) || 'Unknown error during OpenAI request' } as any;
        })();

        await recordErrorAndNotify(extractedError.code, extractedError.message, extractedError.status);

        // attempt fallback to Claude models
        try {
          let fallbackModel = '';
          if (interviewType === 'firstround') {
            fallbackModel = 'claude-sonnet-4-5-20250929';
          } else if (interviewType === 'practice' || interviewType === 'screening') {
            fallbackModel = 'claude-sonnet-4-5-20250929';
          }

          if (fallbackModel) {
            await this.streamClaudeAnswerWithContext(context, botMessageIndex, chatService, onUpdate, abortSignal, fallbackModel, interviewType, true);
            chatService.finalizeConversation();
            return;
          }
        } catch (fallbackErr) {
          if (anyErr?.name === 'AbortError' || anyErr?.code === 20 || anyErr?.type === 'aborted') {
            chatService.finalizeConversation();
            return;
          }
          console.error('Fallback to Claude failed after OpenAI error:', fallbackErr);
        }

        chatService.updateBotMessage(botMessageIndex, 'Something went wrong while generating a response. Please try sending your message again.');
        chatService.finalizeConversation();
        return;
      }
    }
  }


  /**
   * Build context for OpenAI streaming: fetch prompt and chat history from backend, assemble context in frontend
   */
  async buildContext(promptId: string, question: string): Promise<Array<{ role: string; content: string }>> {
    // 1. Fetch system prompt using HttpClient
    const promptData: any = await this.http.get(`${API_URL}/prompt/${promptId}`).toPromise();
    const context: Array<{ role: string; content: string }> = [];
    if (promptData && promptData.prompt) {
      context.push({ role: 'system', content: promptData.prompt });
    }
    // 2. Fetch chat history using HttpClient
    const historyData: any[] = await this.http.get<any[]>(`${API_URL}/chat-history/${encodeURIComponent(promptId)}`).toPromise() || [];
    // 3. Sort by timestamp descending, take last 2 messages
    const sorted = (historyData || []).sort((a: any, b: any) => Number(b.timestamp) - Number(a.timestamp));
    let count = 0;
    for (const chat of sorted) {
      if (count === 2) break;
      if (chat.question) {
        context.push({ role: 'user', content: chat.question });
      }
      if (chat.answer) {
        context.push({ role: 'assistant', content: chat.answer });
      }
      count++;
    }
    // 4. Add current user question
    context.push({ role: 'user', content: question });
    return context;
  }
  async ClaudeBuildContext(promptId: string, question: string): Promise<Array<{ role: string; content: string }>> {
    // 1. Fetch system prompt using HttpClient
    const promptData: any = await this.http.get(`${API_URL}/prompt/${promptId}`).toPromise();
    const context: Array<{ role: string; content: string }> = [];
    // Claude expects the system prompt as the first 'assistant' message
    if (promptData && promptData.prompt) {
      context.push({ role: 'assistant', content: promptData.prompt });
    }
    // 2. Fetch chat history using HttpClient
    const historyData: any[] = await this.http.get<any[]>(`${API_URL}/chat-history/${encodeURIComponent(promptId)}`).toPromise() || [];
    // 3. Sort by timestamp ascending, take last 3-4 Q&A pairs
    const sorted = (historyData || []).sort((a: any, b: any) => Number(a.timestamp) - Number(b.timestamp));
    let pairs = 0;
    for (const chat of sorted.reverse()) {
      if (pairs === 4) break;
      if (chat.question) {
        context.push({ role: 'user', content: chat.question });
      }
      if (chat.answer) {
        context.push({ role: 'assistant', content: chat.answer });
      }
      pairs++;
    }
    // 4. Add current user question
    context.push({ role: 'user', content: question });
    return context;
  }


  async buildContextFromPreloadedData(promptData: any, question: string, promptId: string): Promise<Array<{ role: string; content: string }>> {
    const context: Array<{ role: string; content: string }> = [];

    // Add system prompt as the first assistant message (Claude-style)
    if (promptData && promptData.prompt) {
      context.push({ role: 'assistant', content: promptData.prompt });
    }

    // Fetch chat history for the given promptId (service is responsible for history retrieval)
    const historyData: any[] = await this.http.get<any[]>(`${API_URL}/chat-history/${encodeURIComponent(promptId)}`).toPromise() || [];

    // Sort by timestamp ascending (oldest first) using Date parsing to be robust
    const sorted = (historyData || []).sort((a: any, b: any) => (new Date(a.timestamp).getTime() || 0) - (new Date(b.timestamp).getTime() || 0));

    // Include up to the last 4 Q/A pairs, but push them in oldest->newest order
    const maxPairs = 4;
    const startIndex = Math.max(0, sorted.length - maxPairs);
    for (let i = startIndex; i < sorted.length; i++) {
      const chat = sorted[i];
      if (chat.question) {
        context.push({ role: 'user', content: chat.question });
      }
      if (chat.answer) {
        context.push({ role: 'assistant', content: chat.answer });
      }
    }

    // Add current user question
    context.push({ role: 'user', content: question });
    return context;
  }
  /**
   * Save answer to backend after streaming is complete
   */
async saveAnswer(
  promptId: string,
  question: string,
  answer: string,
  s3Url?: string
): Promise<any> {
  let url = `${API_URL}/save-response`;
  const params = new URLSearchParams({ promptId });

  if (s3Url) {
    params.append('s3Url', s3Url);
  }

  url += `?${params.toString()}`;

  const body = {
    question,
    answer
  };

  try {
    // Convert Observable to Promise so callers using `await` will actually execute and wait
    const resp = await firstValueFrom(this.http.post(url, body));
    return resp;
  } catch (err) {
    console.error('saveAnswer failed', err);
    throw err;
  }
}



  /**
   * Send both text and file URL to OpenAI /v1/responses endpoint using fetch
   */
  sendTextAndFileToOpenAI(inputText: string, fileUrl: string): Observable<any> {
    const openAiApiKey =  this.openAiKey || OPENAI_API_KEY;
    const body = {
      model: 'gpt-5.2',
      input: [
        {
          role: 'user',
          content: [
            { type: 'input_text', text: inputText },
            { type: 'input_file', file_url: fileUrl }
          ]
        }
      ]
    };

    return new Observable(observer => {
      fetch('https://api.openai.com/v1/responses', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${openAiApiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      })
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          return response.json();
        })
        .then(data => {
          observer.next(data);
          observer.complete();
        })
        .catch(error => {
          observer.error(error);
        });
    });
  }

  /**
   * Send both text and image URL to OpenAI /v1/responses endpoint using fetch
   */
  sendTextAndImageToOpenAI(inputText: string, imageUrl: string): Observable<any> {
    const openAiApiKey = this.openAiKey || OPENAI_API_KEY;
    const body = {
      model: 'gpt-5.2',
      input: [
        {
          role: 'user',
          content: [
            { type: 'input_text', text: inputText },
            { type: 'input_image', image_url: imageUrl }
          ]
        }
      ]
    };

    return new Observable(observer => {
      fetch('https://api.openai.com/v1/responses', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${openAiApiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      })
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          return response.json();
        })
        .then(data => {
          observer.next(data);
          observer.complete();
        })
        .catch(error => {
          observer.error(error);
        });
    });
  }

  getOpenApiData(messages: Message[]): Observable<string> {
    const payload = messages.map(msg => ({
      role: msg.sender,
      content: msg.text
    }));
    return this.http.post(`${API_URL}/generate-doc`, payload, { responseType: 'text' });
  }

  getSummary(messages: Message[]): Observable<SummaryResponse> {
    const payload = messages.map(msg => ({
      role: msg.sender,
      content: msg.text,
    }));
    return this.http.post<SummaryResponse>(`${API_URL}/summarize`, payload);
  }
  getAllPrompts(): Observable<PromptMemory[]> {
    return this.http.get<PromptMemory[]>(`${API_URL}/prompts`);
  }
  askQuestion(dto: QuestionDto): Observable<any> {
    return this.http.post(`${API_URL}/ask`, dto);
  }
  getPromptById(id: string): Observable<PromptMemory> {
    return this.http.get<PromptMemory>(`${API_URL}/prompt/${id}`);
  }
  updatePrompt(id: string, prompt: string): Observable<string> {
    return this.http.put(`${API_URL}/prompt/${id}`, { id, prompt }, { responseType: 'text' });
  }
  deletePrompt(id: string): Observable<string> {
    return this.http.delete(`${API_URL}/prompt/${id}`, { responseType: 'text' });
  }
  getAllChatHistory(chatId: string): Observable<ChatHistory[]> {
    return this.http.get<ChatHistory[]>(`${API_URL}/chat-history/${chatId}`);
  }
  getChatHistoryByThread(chatThreadId: string): Observable<ChatHistory[]> {
    return this.http.get<ChatHistory[]>(`${API_URL}/history/thread/${chatThreadId}`);
  }
  getChatHistoryByPromptId(promptId: string): Observable<ChatHistory[]> {
    return this.http.get<ChatHistory[]>(`${API_URL}/chat-history/${promptId}`);
  } 

  
  getUserChatHistoryByPromptId(promptId: string): Observable<ChatHistory[]> {
    return this.http.get<ChatHistory[]>(`${API_URL}/user/chathistory/${promptId}`);
  }
  deleteChatHistory(id: string): Observable<string> {
    return this.http.delete(`${API_URL}/history/${id}`, { responseType: 'text' });
  }
  getAiKeyStatus(): Observable<aiKeyStatusDTO[]> {
    return this.http.get<aiKeyStatusDTO[]>(`${API_URL}/aiKeyStatus/keys/all`);
  }

  createAiKeyStatus(payload: aiKeyStatusDTO): Observable<aiKeyStatusDTO> {
    return this.http.post<aiKeyStatusDTO>(`${API_URL}/aiKeyStatus/keys`, payload);
  }

  updateAiKeyStatus(id: string, payload: aiKeyStatusDTO): Observable<aiKeyStatusDTO> {
    return this.http.put<aiKeyStatusDTO>(`${API_URL}/aiKeyStatus/keys/${id}`, payload);
  } 

  getApiKeys(): Observable<ApiKeys> {
    return this.http.get<ApiKeys>(`${API_URL}/keys/ai`);
  }

  aikeyStatusEmailSend(errorCode: string, errorMessage: string, statusCode?: string | number, aiService?: string): Observable<string> {
    try {
      // Prefer explicit statusCode if provided (comes from response headers or error.status),
      // otherwise fall back to provided errorCode string.
      const codeStr = String(statusCode ?? errorCode ?? '');
      // Determine recipients: if 401 or 429 -> all configured emails; otherwise only Jagadish & Sarath
      const cfg: any = emails || {};
      const allEmails = Object.values(cfg).filter(Boolean) as string[];
      const jagadish = cfg['Jagadish'] || cfg['jagadish'] || '';
      const sarath = cfg['Sarath'] || cfg['sarath'] || '';

      let recipientsEmails: string[] = [];
      if (codeStr.includes('401') || codeStr.includes('429')) {
        recipientsEmails = allEmails;
      } else {
        recipientsEmails = [jagadish, sarath].filter(Boolean);
      }

      // Build userInfoList array matching the curl format: [{ email, name }, ...]
      const userInfoList = recipientsEmails.map(email => {
        // try to infer the configured key/name from the config map, otherwise fallback to empty name
        const nameKey = Object.keys(cfg).find(k => cfg[k] === email) || '';
        const name = nameKey || (email === jagadish ? 'Jagadish' : (email === sarath ? 'Sarath' : ''));
        return { email, name };
      });

      const body = {
        errorCode: codeStr,
        errorMessage: String(errorMessage || ''),
        userInfoList,
        aiService: aiService || ''
      };

      const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
      return this.http.post<string>(`${API_URL}/aiKeyStatus/keys/check-api-key-status`, body, { headers });
    } catch (e) {
      // Fallback: if JSON build/send fails for any reason, fall back to form-url-encoded for robustness
      try {
        const cfg: any = emails || {};
        const jagadish = cfg['Jagadish'] || cfg['jagadish'] || '';
        const sarath = cfg['Sarath'] || cfg['sarath'] || '';
        let params = new HttpParams().set('errorCode', String(statusCode ?? errorCode ?? '')).set('errorMessage', String(errorMessage || ''));
        const fallRecipients = [jagadish, sarath].filter(Boolean);
        for (const r of fallRecipients) {
          params = params.append('emailAddresses[]', r);
        }
        const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
        return this.http.post<string>(`${API_URL}/aiKeyStatus/keys/check-api-key-status`, params.toString(), { headers });
      } catch (e2) {
        // Last-resort: send as simple request params using HttpParams with string values
        const lastParams = new HttpParams()
          .set('errorCode', String(errorCode ?? ''))
          .set('errorMessage', String(errorMessage ?? ''))
          .set('aiService', String(aiService ?? ''));
        // send an empty body string to ensure response typing aligns with Observable<string>
        return this.http.post<string>(`${API_URL}/aiKeyStatus/keys/check-api-key-status`, '', { params: lastParams });
      }
    }
  }

  /**
   * Refresh stored AI key statuses: if any record is inactive for >= thresholdMinutes,
   * mark it active again using the backend.
   */
  async refreshAiKeyStatusesIfNeeded(thresholdMinutes: number = 480): Promise<any> {
    try {
      const statuses: any = await this.getAiKeyStatus().pipe(take(1)).toPromise();
      const now = Date.now();
      this.serviceStatus = statuses;
      console.log('refreshAiKeyStatusesIfNeeded: fetched statuses', statuses);
      const thresholdMs = thresholdMinutes * 60 * 1000;
      for (const s of (statuses || [])) {
        try {
          if (s && String(s.status).toLowerCase() === 'inactive') {
            const created = Number(s.createdAT) || 0;
            if (now - created >= thresholdMs) {
              // Reactivating: mark active, clear any prior error message and refresh createdAT
              const updated: aiKeyStatusDTO = { ...s, status: 'active', error: '', createdAT: Date.now() } as any;
              await this.updateAiKeyStatus(s.id, updated).pipe(take(1)).toPromise();
              const statuses: any = await this.getAiKeyStatus().pipe(take(1)).toPromise();
              this.serviceStatus = statuses;
            }
          }
        } catch (e) {
          console.warn('refreshAiKeyStatusesIfNeeded: single record update failed', e);
        }
      }

      return Array.isArray(statuses) ? statuses : [];
    } catch (e) {
      console.warn('refreshAiKeyStatusesIfNeeded failed', e);
    }
  }

  /**
   * Public helper: refresh statuses (reactivate stale inactive records) and return the latest statuses.
   * Useful for callers that want to ensure statuses are up-to-date before using them.
   */
  async refreshAndGetAiKeyStatus(thresholdMinutes: number = 480): Promise<aiKeyStatusDTO[]> {
    try {
      const status = await this.refreshAiKeyStatusesIfNeeded(thresholdMinutes);
      // const statuses: any = await this.getAiKeyStatus().pipe(take(1)).toPromise();
      // return Array.isArray(statuses) ? statuses : [];
      return Array.isArray(status) ? status : [];
    } catch (e) {
      console.warn('refreshAndGetAiKeyStatus failed', e);
      return [];
    }
  }

  /**
   * Create or update an aiKeyStatus record for a given service and send notification.
   * If a record exists for the service, update it; otherwise create a new one.
   */
  private async upsertAiKeyStatus(serviceName: string, errorCode: string, errorMessage: string, statusCode?: string | number): Promise<void> {
    try {
      const statuses: any = await this.getAiKeyStatus().pipe(take(1)).toPromise();
      this.serviceStatus = statuses
      const safeStatuses = Array.isArray(statuses) ? statuses : [];
      const existing = safeStatuses.find((s: any) => String((s.aiService || s.aiservice || '')).toLowerCase() === serviceName.toLowerCase());
      const payload: aiKeyStatusDTO = {
        id: existing?.id || '',
        error: errorMessage,
        status: 'inactive',
        createdAT: Date.now(),
        aiService: serviceName
      } as any;

      if (existing && existing.id) {
        try {
          await this.updateAiKeyStatus(existing.id, payload).pipe(take(1)).toPromise();
          const statuses: any = await this.getAiKeyStatus().pipe(take(1)).toPromise();
          this.serviceStatus = statuses
        } catch (e) {
          console.warn('upsertAiKeyStatus update failed, trying create', e);
          await this.createAiKeyStatus(payload).pipe(take(1)).toPromise();
        }
      } else {
        await this.createAiKeyStatus(payload).pipe(take(1)).toPromise();
      }

      // send notification (best-effort). Pass statusCode if available so recipient logic can use it.
      try {
        await this.aikeyStatusEmailSend(errorCode, errorMessage, statusCode, serviceName).pipe(take(1)).toPromise();
      } catch (e) {
        console.warn('upsertAiKeyStatus: email send failed', e);
      }
    } catch (e) {
      console.warn('upsertAiKeyStatus failed', e);
    }
  }

  /**
   * Extracts the output text from OpenAI /v1/responses API response
   */
  extractOpenAIText(response: any): string {
    // Handles both file and image response format
    if (response && response.output && Array.isArray(response.output) && response.output.length > 0) {
      const msg = response.output[0];
      if (msg && msg.content && Array.isArray(msg.content)) {
        for (const item of msg.content) {
          if (item.type === 'output_text' && item.text) {
            return item.text;
          }
        }
      }
    }
    // fallback: try response.content.text (for legacy or direct format)
    if (response && response.content && response.content.text) {
      return response.content.text;
    }
    return '';
  } 

  setKeys(openAiKey: string, claudeAiKey: string) {
    this.openAiKey = openAiKey;
    this.claudeAiKey = claudeAiKey;
  } 


//   // Overlay api 

//   getChatHistory(promptId: string): Observable<ChatHistory[]> {
//     return this.http.get<ChatHistory[]>(`${API_URL}/chat-history/${promptId}`);
//   }  

//   async saveAiAnswer(
//   promptId: string,
//   question: string,
//   answer: string,
//   s3Url?: string
// ): Promise<any> {
//   let url = `${API_URL}/save-response`;
//   const params = new URLSearchParams({ promptId });

//   if (s3Url) {
//     params.append('s3Url', s3Url);
//   }

//   url += `?${params.toString()}`;

//   const body = {
//     question,
//     answer
//   };

//   try {
//     // Convert Observable to Promise so callers using `await` will actually execute and wait
//     const resp = await firstValueFrom(this.http.post(url, body));
//     return resp;
//   } catch (err) {
//     console.error('saveAnswer failed', err);
//     throw err;
//   }
// } 
 

//  getAiKeysStatus(): Observable<aiKeyStatusDTO[]> {
//     return this.http.get<aiKeyStatusDTO[]>(`${API_URL}/aiKeyStatus/keys/all`);
//   }

//   createAiKeysStatus(payload: aiKeyStatusDTO): Observable<aiKeyStatusDTO> {
//     return this.http.post<aiKeyStatusDTO>(`${API_URL}/aiKeyStatus/keys`, payload);
//   }

//   updateAiKeysStatus(id: string, payload: aiKeyStatusDTO): Observable<aiKeyStatusDTO> {
//     return this.http.put<aiKeyStatusDTO>(`${API_URL}/aiKeyStatus/keys/${id}`, payload);
//   } 

//   getApiKey(): Observable<ApiKeys> {
//     return this.http.get<ApiKeys>(`${API_URL}/keys/ai`);
//   }

//   aikeysStatusEmailSend(errorCode: string, errorMessage: string, statusCode?: string | number, aiService?: string): Observable<string> {
//     try {
//       // Prefer explicit statusCode if provided (comes from response headers or error.status),
//       // otherwise fall back to provided errorCode string.
//       const codeStr = String(statusCode ?? errorCode ?? '');
//       // Determine recipients: if 401 or 429 -> all configured emails; otherwise only Jagadish & Sarath
//       const cfg: any = emails || {};
//       const allEmails = Object.values(cfg).filter(Boolean) as string[];
//       const jagadish = cfg['Jagadish'] || cfg['jagadish'] || '';
//       const sarath = cfg['Sarath'] || cfg['sarath'] || '';

//       let recipientsEmails: string[] = [];
//       if (codeStr.includes('401') || codeStr.includes('429')) {
//         recipientsEmails = allEmails;
//       } else {
//         recipientsEmails = [jagadish, sarath].filter(Boolean);
//       }

//       // Build userInfoList array matching the curl format: [{ email, name }, ...]
//       const userInfoList = recipientsEmails.map(email => {
//         // try to infer the configured key/name from the config map, otherwise fallback to empty name
//         const nameKey = Object.keys(cfg).find(k => cfg[k] === email) || '';
//         const name = nameKey || (email === jagadish ? 'Jagadish' : (email === sarath ? 'Sarath' : ''));
//         return { email, name };
//       });

//       const body = {
//         errorCode: codeStr,
//         errorMessage: String(errorMessage || ''),
//         userInfoList,
//         aiService: aiService || ''
//       };

//       const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
//       return this.http.post<string>(`${API_URL}/aiKeyStatus/keys/check-api-key-status`, body, { headers });
//     } catch (e) {
//       // Fallback: if JSON build/send fails for any reason, fall back to form-url-encoded for robustness
//       try {
//         const cfg: any = emails || {};
//         const jagadish = cfg['Jagadish'] || cfg['jagadish'] || '';
//         const sarath = cfg['Sarath'] || cfg['sarath'] || '';
//         let params = new HttpParams().set('errorCode', String(statusCode ?? errorCode ?? '')).set('errorMessage', String(errorMessage || ''));
//         const fallRecipients = [jagadish, sarath].filter(Boolean);
//         for (const r of fallRecipients) {
//           params = params.append('emailAddresses[]', r);
//         }
//         const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
//         return this.http.post<string>(`${API_URL}/aiKeyStatus/keys/check-api-key-status`, params.toString(), { headers });
//       } catch (e2) {
//         // Last-resort: send as simple request params using HttpParams with string values
//         const lastParams = new HttpParams()
//           .set('errorCode', String(errorCode ?? ''))
//           .set('errorMessage', String(errorMessage ?? ''))
//           .set('aiService', String(aiService ?? ''));
//         // send an empty body string to ensure response typing aligns with Observable<string>
//         return this.http.post<string>(`${API_URL}/aiKeyStatus/keys/check-api-key-status`, '', { params: lastParams });
//       }
//     }
//   }

//   /**
//    * Refresh stored AI key statuses: if any record is inactive for >= thresholdMinutes,
//    * mark it active again using the backend.
//    */
//   async refreshAiKeysStatusesIfNeeded(thresholdMinutes: number = 480): Promise<any> {
//     try {
//       const statuses: any = await this.getAiKeysStatus().pipe(take(1)).toPromise();
//       const now = Date.now();
//       this.serviceStatus = statuses;
//       console.log('refreshAiKeyStatusesIfNeeded: fetched statuses', statuses);
//       const thresholdMs = thresholdMinutes * 60 * 1000;
//       for (const s of (statuses || [])) {
//         try {
//           if (s && String(s.status).toLowerCase() === 'inactive') {
//             const created = Number(s.createdAT) || 0;
//             if (now - created >= thresholdMs) {
//               // Reactivating: mark active, clear any prior error message and refresh createdAT
//               const updated: aiKeyStatusDTO = { ...s, status: 'active', error: '', createdAT: Date.now() } as any;
//               await this.updateAiKeysStatus(s.id, updated).pipe(take(1)).toPromise();
//               const statuses: any = await this.getAiKeysStatus().pipe(take(1)).toPromise();
//               this.serviceStatus = statuses;
//             }
//           }
//         } catch (e) {
//           console.warn('refreshAiKeysStatusesIfNeeded: single record update failed', e);
//         }
//       }

//       return Array.isArray(statuses) ? statuses : [];
//     } catch (e) {
//       console.warn('refreshAiKeysStatusesIfNeeded failed', e);
//     }
//   }

//   /**
//    * Public helper: refresh statuses (reactivate stale inactive records) and return the latest statuses.
//    * Useful for callers that want to ensure statuses are up-to-date before using them.
//    */
//   async refreshAndGetAiKeysStatus(thresholdMinutes: number = 480): Promise<aiKeyStatusDTO[]> {
//     try {
//       const status = await this.refreshAiKeysStatusesIfNeeded(thresholdMinutes);
//       // const statuses: any = await this.getAiKeyStatus().pipe(take(1)).toPromise();
//       // return Array.isArray(statuses) ? statuses : [];
//       return Array.isArray(status) ? status : [];
//     } catch (e) {
//       console.warn('refreshAndGetAiKeysStatus failed', e);
//       return [];
//     }
//   }

//   /**
//    * Create or update an aiKeyStatus record for a given service and send notification.
//    * If a record exists for the service, update it; otherwise create a new one.
//    */
//   private async upsertAiKeysStatus(serviceName: string, errorCode: string, errorMessage: string, statusCode?: string | number): Promise<void> {
//     try {
//       const statuses: any = await this.getAiKeysStatus().pipe(take(1)).toPromise();
//       this.serviceStatus = statuses
//       const safeStatuses = Array.isArray(statuses) ? statuses : [];
//       const existing = safeStatuses.find((s: any) => String((s.aiService || s.aiservice || '')).toLowerCase() === serviceName.toLowerCase());
//       const payload: aiKeyStatusDTO = {
//         id: existing?.id || '',
//         error: errorMessage,
//         status: 'inactive',
//         createdAT: Date.now(),
//         aiService: serviceName
//       } as any;

//       if (existing && existing.id) {
//         try {
//           await this.updateAiKeysStatus(existing.id, payload).pipe(take(1)).toPromise();
//           const statuses: any = await this.getAiKeysStatus().pipe(take(1)).toPromise();
//           this.serviceStatus = statuses
//         } catch (e) {
//           console.warn('upsertAiKeysStatus update failed, trying create', e);
//           await this.createAiKeysStatus(payload).pipe(take(1)).toPromise();
//         }
//       } else {
//         await this.createAiKeysStatus(payload).pipe(take(1)).toPromise();
//       }

//       // send notification (best-effort). Pass statusCode if available so recipient logic can use it.
//       try {
//         await this.aikeysStatusEmailSend(errorCode, errorMessage, statusCode, serviceName).pipe(take(1)).toPromise();
//       } catch (e) {
//         console.warn('upsertAiKeysStatus: email send failed', e);
//       }
//     } catch (e) {
//       console.warn('upsertAiKeysStatus failed', e);
//     }
//   }





}
